import React from 'react';
import { cn } from './cn';

export type ProgressTone = 'brand' | 'success' | 'warning' | 'critical' | 'neutral';

const TRACK = 'bg-surface-2';
const FILL: Record<ProgressTone, string> = {
  brand:    'bg-brand',
  success:  'bg-sev-maintenance',
  warning:  'bg-sev-major',
  critical: 'bg-sev-critical',
  neutral:  'bg-ink-subtle',
};

export interface ProgressBarProps {
  value: number;
  max?: number;
  tone?: ProgressTone;
  /** Optional label shown above the bar. */
  label?: React.ReactNode;
  /** Show "value / max" counter at the end of the label row. */
  showCount?: boolean;
  /** Height of the track. */
  size?: 'sm' | 'md';
  className?: string;
  ariaLabel?: string;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value, max = 100, tone = 'brand', label, showCount = false, size = 'sm', className, ariaLabel,
}) => {
  const clamped = Math.max(0, Math.min(value, max));
  const pct = max > 0 ? (clamped / max) * 100 : 0;
  return (
    <div className={cn('w-full', className)}>
      {(label || showCount) && (
        <div className="flex items-center justify-between mb-1">
          {label && <span className="text-caption text-ink-muted font-medium">{label}</span>}
          {showCount && (
            <span className="text-caption text-ink-subtle tabular-nums">
              {clamped}/{max}
            </span>
          )}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
        className={cn(
          'w-full rounded-full overflow-hidden',
          size === 'sm' ? 'h-1.5' : 'h-2',
          TRACK,
        )}
      >
        <div
          className={cn('h-full rounded-full transition-[width] duration-300 ease-premium', FILL[tone])}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};
